import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  ButtonGroup,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Toolbar,
  Typography,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import moment from 'moment';
import { QueryParams } from 'renderer/shared/schemas/common.schemas';
import { useAppDispatch, useAppSelector } from 'renderer/shared/state/hooks';
import { formatCurrency } from 'renderer/shared/formatters/formatters';
import { useListTransactionsQuery } from '../services/transactions.endpoints';
import { selectTransactionQuery } from '../state/transaction.selectors';
import { setTransactionQuery } from '../state/transactions.slice';
import RefundDialog from './RefundDialog';
import { BaseTransaction } from '../schemas/transaction.schemas';

interface EnhancedTableToolbarProps {
  onSearchChange: (value: string) => void;
  onSearch: () => void;
  onReset: () => void;
  sortBy: string;
  setSortBy: (value: string) => void;
  sortOrder: 'asc' | 'desc';
  // setSortOrder: (value: 'asc' | 'desc') => void;
  setSortOrder: () => void;
}

const EnhancedTableToolbar: React.FC<EnhancedTableToolbarProps> = ({
  onSearchChange,
  onSearch,
  onReset,
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder,
}) => {
  return (
    <Toolbar
      sx={{
        justifyContent: 'flex-start',
        padding: '8px 0',
        borderBottom: '1px solid rgba(224, 224, 224, 1)',
        backgroundColor: 'white',
      }}
    >
      <TextField
        variant="outlined"
        placeholder="Search"
        size="small"
        sx={{
          flexGrow: 1,
          '& .MuiOutlinedInput-root': {
            borderRadius: '4px',
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'primary.main',
            },
          },
        }}
        // value={search}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          onChange: (event) => onSearchChange(event.target.value),
        }}
      />
      <Button variant="outlined" onClick={onSearch} sx={{ mx: 1 }}>
        Search
      </Button>
      <Button variant="outlined" onClick={onReset}>
        Reset
      </Button>
      <Box sx={{ flexGrow: 1 }} />
      <Select
        value={sortBy}
        onChange={(e) => setSortBy(e.target.value)}
        sx={{
          minWidth: 120,
          '& .MuiSelect-select': {
            padding: '8px',
            borderRadius: '4px',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0, 0, 0, 0.23)',
          },
          marginRight: '16px',
        }}
      >
        <MenuItem value="firstName">First Name</MenuItem>
        <MenuItem value="lastName">Last Name</MenuItem>
        <MenuItem value="email">Email</MenuItem>
        <MenuItem value="createdAt">Created Date</MenuItem>
      </Select>
      <ButtonGroup sx={{ marginRight: 4 }}>
        <Button
          variant={sortOrder === 'asc' ? 'contained' : 'outlined'}
          onClick={() => setSortOrder()}
          size="small"
        >
          Asc
        </Button>
        <Button
          variant={sortOrder === 'desc' ? 'contained' : 'outlined'}
          onClick={() => setSortOrder()}
          size="small"
        >
          Desc
        </Button>
      </ButtonGroup>
    </Toolbar>
  );
};

const TransactionList = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const query = useAppSelector(selectTransactionQuery);
  const dispatch = useAppDispatch();
  const setQuery = (q: QueryParams) => dispatch(setTransactionQuery(q));
  // const [query, setQuery] = useState<QueryParams>({
  //   page: 1,
  //   limit: 10,
  //   sortBy: 'createdAt',
  //   sortOrder: 'desc',
  //   search: '',
  // });

  // useEffect(() => {
  //   console.log('useEffect', query); // Log to see how query changes when you toggle sortOrder
  // }, [query]);

  const {
    data: transactions,
    isLoading,
    isError,
    error,
  } = useListTransactionsQuery(query, {
    refetchOnMountOrArgChange: true,
  });

  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] =
    useState<BaseTransaction | null>(null);

  const handleSearch = () => {
    setQuery({ ...query, search: searchQuery, page: 1 });
  };

  const handleReset = () => {
    setSearchQuery('');
    setQuery({ ...query, search: '' });
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setQuery({ ...query, page: newPage + 1 });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setQuery({ ...query, limit: parseInt(event.target.value, 10), page: 1 });
  };

  const setSortBy = (sortBy: string) => {
    setQuery({ ...query, sortBy });
  };

  const setSortOrder = () => {
    setQuery({
      ...query,
      sortOrder: query.sortOrder === 'asc' ? 'desc' : 'asc',
    });
  };

  const avatarStyle = () => ({
    bgcolor: '#0426ae',
    boxShadow: '0 2px 4px 0 rgba(0,0,0,0.12)',
  });

  const getInitials = (firstName: string, lastName: string) => {
    const firstInitial = firstName ? firstName[0].toUpperCase() : '';
    const lastInitial = lastName ? lastName[0].toUpperCase() : '';
    return `${firstInitial}${lastInitial}`;
  };

  const formatDateAndTime = (dateString: string) => {
    const date = moment(dateString);
    return {
      formattedDate: date.format('MMM Do YYYY'),
      formattedTime: date.format('h:mm A'),
    };
  };

  if (isLoading) return <div>Loading...</div>;
  if (isError) return <div>Error: {'status' in error ? error.status : ''}</div>;
  // if (!transactions || transactions.totalDocs === 0)
  //   return <div>No transactions found</div>;

  return (
    <Stack spacing={1} sx={{ pt: 0, pr: 3, pb: 3, pl: 3 }}>
      <Box>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
          <MuiLink underline="hover" color="inherit" href="/#">
            Home
          </MuiLink>
          <Typography color="text.primary">Transactions</Typography>
        </Breadcrumbs>

        <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
          Transactions
        </Typography>

        <Paper sx={{ width: '100%', mb: 2, overflow: 'hidden' }}>
          <EnhancedTableToolbar
            // search={query.search}
            onSearchChange={handleSearchChange}
            onSearch={handleSearch}
            onReset={handleReset}
            sortBy={query.sortBy}
            setSortBy={setSortBy}
            sortOrder={query.sortOrder}
            setSortOrder={setSortOrder}
          />
          <TableContainer>
            <Table
              sx={{
                '& .MuiTableCell-head': {
                  backgroundColor: 'rgba(224, 224, 224, 1)',
                  color: 'black',
                  fontWeight: 'bold',
                },
                '& .MuiTableCell-body': {
                  fontSize: '0.875rem',
                  '& .MuiAvatar-root': {
                    verticalAlign: 'middle',
                    marginRight: '8px',
                  },
                },
                '& .MuiIconButton-root': {
                  padding: '8px',
                },
                '& .MuiTableRow-root:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.07)',
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>User Info</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!transactions || transactions.totalDocs === 0 ? (
                  <TableRow>
                    <TableCell>
                      {' '}
                      <div>No transactions found</div>
                    </TableCell>
                  </TableRow>
                ) : (
                  transactions.docs.map(
                    (
                      {
                        _id: id,
                        productName,
                        category,
                        amount,
                        refundedAmount,
                        status,
                        email,
                        firstName,
                        lastName,
                        createdAt,
                      },
                      i,
                    ) => (
                      <TableRow key={id}>
                        <TableCell>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <Avatar sx={avatarStyle()}>
                              {getInitials(firstName || '', lastName || '')}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" noWrap>
                                {`${firstName} ${lastName}`}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                noWrap
                                sx={{ fontSize: '0.8rem' }}
                              >
                                {email}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <Box>
                              <Typography variant="subtitle2" noWrap>
                                {productName}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                noWrap
                                sx={{ fontSize: '0.8rem' }}
                              >
                                {category}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              component="span"
                            >
                              {formatDateAndTime(createdAt).formattedDate}
                            </Typography>
                            <br />
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              component="span"
                            >
                              {formatDateAndTime(createdAt).formattedTime}
                            </Typography>
                          </Box>
                        </TableCell>
                        {refundedAmount > 0 ? (
                          <Tooltip
                            title={`Refunded ${formatCurrency(refundedAmount)}`}
                            // placement="right-start"
                          >
                            <TableCell sx={{ color: '#d32f2f' }}>
                              {formatCurrency(amount)}
                            </TableCell>
                          </Tooltip>
                        ) : (
                          <TableCell>{formatCurrency(amount)}</TableCell>
                        )}
                        <TableCell>{status}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Process Refund">
                            <IconButton
                              color="error"
                              onClick={() => {
                                setSelectedTransaction(transactions.docs[i]);
                                setRefundModalOpen(true);
                              }}
                            >
                              <MoneyOffIcon />
                            </IconButton>
                          </Tooltip>
                          <Link to={`/transactions/${id}`}>
                            <IconButton color="info">
                              <VisibilityIcon />
                            </IconButton>
                          </Link>
                        </TableCell>
                      </TableRow>
                    ),
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <RefundDialog
            open={refundModalOpen}
            setOpen={setRefundModalOpen}
            transaction={selectedTransaction}
          />
          <TablePagination
            component="div"
            count={transactions?.totalDocs || 0}
            page={query.page - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPage={query.limit}
            rowsPerPageOptions={[5, 10, 25]}
            sx={{
              '.MuiTablePagination-toolbar': {
                minHeight: '52px',
                paddingRight: '32px',
              },
              '.MuiTablePagination-selectLabel': {
                color: 'rgba(0, 0, 0, 0.54)',
                lineHeight: '1.4375em',
                marginRight: '8px',
              },
              '.MuiTablePagination-select': {
                marginRight: '32px',
              },
              '.MuiTablePagination-displayedRows': {
                flex: 'none',
                marginRight: 'auto',
              },
              '.MuiTablePagination-actions': {
                marginLeft: 0,
                '& .MuiIconButton-root': {
                  padding: '8px',
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                },
                '& .Mui-disabled': {
                  opacity: 0.3,
                },
              },
              '.MuiTablePagination-actions > button': {
                color: 'rgba(0, 0, 0, 0.54)',
              },
            }}
          />
        </Paper>
      </Box>
    </Stack>
  );
};

export default TransactionList;
